<template>
  <div class="wrapper" ref="colorPickerWrapper">
    <div
      :class="`picker-wrapper picker-wrapper--${position} ${hideAlphaSlider ? 'hide-alpha-slider' : ''}`"
      v-show="active">
      <v-color-picker
        v-if="hasActivated"
        :value="value"
        class="mx-auto"
        dot-size="20"
        hide-mode-switch
        mode="hexa"
        elevation="0"
        @update:color="updateColor">
      </v-color-picker>
    </div>
    <v-text-field
      :error="hasErrors"
      hide-details
      v-model="textValue"
      @mouseenter="$emit('is-hovered', true)"
      @mouseleave="$emit('is-hovered', false)"
      @focus="toggleColorPicker(true)"
      @change="handleColorChange">
      <template v-slot:label> {{ componentTitle }}</template>
      <template v-slot:append>
        <div
          class="field-picker__color-preview cursor-pointer"
          :style="{
            'background-color': value,
          }"></div>
      </template>
    </v-text-field>
    <div v-if="hasErrors" class="v-messages mt-2 error--text">
      <div class="v-messages__wrapper">
        <div class="v-messages__message">
          <template v-for="error in commonProps.errorMessages">
            {{ error }}
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  /**
   * Needs rework, when unmounted, emits #FF0000 color.
   */
  import { ref, watch, computed, getCurrentInstance } from "@vue/composition-api";
  import { onClickOutside } from "@vueuse/core";
  import Input from "@inulti/vuetify-admin/src/mixins/input";

  export default {
    name: "VaColorPicker",
    mixins: [Input],
    props: {
      value: {
        type: String,
        default: "#FFFFFF",
      },
      title: {
        type: String,
        required: false,
        default: "",
      },
      mode: {
        type: String,
        default: "hex",
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      position: {
        type: String,
        default: "top",
      },
      hideAlphaSlider: {
        type: Boolean,
        default: false,
      },
      allowEmpty: {
        type: Boolean,
        default: false,
      },
    },
    setup(props, { emit }) {
      const vm = getCurrentInstance().proxy;
      const componentTitle = ref(props.title);
      const textValue = ref(props.value);
      const active = ref(false);
      // Defer mounting the heavy v-color-picker until first opened.
      const hasActivated = ref(false);
      const colorPickerWrapper = ref(null);
      const emitCount = ref(0);
      const isWaitingForDefaultRed = ref(!props.value);

      const updateColor = colorCode => {
        // updateColor method is called when v-color-picker is mounted. It automatically emits
        // #FF0000 value, thus the va-color-picker component cannot have empty string as a value
        // (let's say in cases when we need to send empty value instead of some default color).
        // We have to skip the one red that vuetify emits right after it receives an empty value.
        const isDefaultRed =
          isWaitingForDefaultRed.value &&
          (colorCode[props.mode] === "#FF0000" || colorCode[props.mode] === "#FF0000FF");
        isWaitingForDefaultRed.value = false;
        if (isDefaultRed && (props.allowEmpty || emitCount.value === 0)) {
          return;
        }

        emitCount.value++;
        emit("input", colorCode[props.mode]);
      };

      const toggleColorPicker = state => {
        if (state) hasActivated.value = true;
        active.value = state;
        emit("is-active", state);
      };

      const handleColorChange = value => {
        emit("input", value);
      };

      onClickOutside(colorPickerWrapper, () => {
        toggleColorPicker(false);
      });

      const hasErrors = computed(() => {
        // If there's a server-side error
        if (vm.commonProps.errorMessages.length > 0) return true;

        return false;
      });

      watch(
        () => props.value,
        () => {
          textValue.value = props.value;
          isWaitingForDefaultRed.value = !props.value;
        },
      );

      return {
        colorPickerWrapper,
        componentTitle,
        updateColor,
        toggleColorPicker,
        handleColorChange,
        textValue,
        active,
        hasActivated,
        hasErrors,
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  .field-picker__color-preview {
    width: 24px;
    height: 24px;
    border: 1px solid get-color("border");
    border-radius: 50%;
    margin-right: 5px;
  }

  .wrapper {
    position: relative;
    width: 100%;
  }

  @include theme(v-application) using ($material) {
    .picker-wrapper {
      position: absolute;
      z-index: 50;
      filter: drop-shadow(0px 2px 10px map-deep-get($material, "shadow-color-secondary"));
      border-radius: 4px;
      padding: 10px;
      background-color: get-color("element-bg-100");
      right: 0;

      &--top {
        top: -320px;

        &::after {
          bottom: -10px;
          right: 11px;
          border-top: 10px solid get-color("element-bg-100");
        }
      }

      &.hide-alpha-slider {
        &::v-deep {
          .v-color-picker__alpha {
            display: none;
          }

          .v-color-picker__hue {
            margin-bottom: 0 !important;
          }
        }
      }

      &--bottom {
        bottom: -320px;

        &::after {
          top: -10px;
          right: 11px;
          border-bottom: 10px solid get-color("element-bg-100");
        }
      }

      .v-color-picker {
        background-color: get-color("element-bg-100");
      }

      &::after {
        content: "";
        position: absolute;
        width: 0;
        height: 0;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
      }
    }
  }
</style>
